'use client';

import React, { useState } from 'react';
import { MODULARITY_SECTION } from '@/data/homepage';

type StepId = 'complete' | 'components' | 'assembly' | 'installed' | 'remove' | 'replace';

const STEP_IDS: StepId[] = ['complete', 'components', 'assembly', 'installed', 'remove', 'replace'];

// SVG shapes for each step — simple geometric representations
const STEP_VISUALS: Record<StepId, React.ReactNode> = {
  complete: (
    <svg viewBox="0 0 120 80" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" className="w-full h-full">
      {/* Cabinet body */}
      <rect x="10" y="10" width="100" height="60" rx="1" stroke="currentColor" strokeWidth="1.5" fill="var(--muted)" />
      {/* Left door */}
      <rect x="14" y="14" width="44" height="52" rx="1" stroke="currentColor" strokeWidth="1" fill="var(--card)" />
      {/* Right door */}
      <rect x="62" y="14" width="44" height="52" rx="1" stroke="currentColor" strokeWidth="1" fill="var(--card)" />
      {/* Handles */}
      <line x1="52" y1="37" x2="52" y2="43" stroke="var(--primary)" strokeWidth="2" strokeLinecap="round" />
      <line x1="68" y1="37" x2="68" y2="43" stroke="var(--primary)" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  components: (
    <svg viewBox="0 0 120 80" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" className="w-full h-full">
      {/* Exploded parts */}
      <rect x="10" y="8" width="100" height="8" rx="1" stroke="currentColor" strokeWidth="1.5" fill="var(--muted)" />
      <rect x="10" y="22" width="8" height="44" rx="1" stroke="currentColor" strokeWidth="1.5" fill="var(--muted)" />
      <rect x="102" y="22" width="8" height="44" rx="1" stroke="currentColor" strokeWidth="1.5" fill="var(--muted)" />
      <rect x="10" y="64" width="100" height="8" rx="1" stroke="currentColor" strokeWidth="1.5" fill="var(--muted)" />
      {/* Shelf */}
      <rect x="22" y="40" width="76" height="6" rx="1" stroke="currentColor" strokeWidth="1" strokeDasharray="3 2" fill="none" />
      {/* Dots indicating separation */}
      <circle cx="60" cy="36" r="2" fill="var(--primary)" opacity="0.5" />
      <circle cx="60" cy="44" r="2" fill="var(--primary)" opacity="0.5" />
    </svg>
  ),
  assembly: (
    <svg viewBox="0 0 120 80" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" className="w-full h-full">
      {/* Partially assembled */}
      <rect x="10" y="8" width="100" height="8" rx="1" stroke="currentColor" strokeWidth="1.5" fill="var(--muted)" />
      <rect x="10" y="16" width="8" height="56" rx="1" stroke="currentColor" strokeWidth="1.5" fill="var(--muted)" />
      <rect x="102" y="16" width="8" height="56" rx="1" stroke="currentColor" strokeWidth="1.5" fill="var(--muted)" />
      <rect x="10" y="64" width="100" height="8" rx="1" stroke="currentColor" strokeWidth="1.5" fill="var(--muted)" />
      {/* Shelf being placed */}
      <rect x="22" y="38" width="76" height="6" rx="1" stroke="var(--primary)" strokeWidth="1.5" fill="none" />
      {/* Assembly arrows */}
      <path d="M55 30 L55 36" stroke="var(--primary)" strokeWidth="1.5" strokeLinecap="round" markerEnd="url(#arrow)" />
      <path d="M65 30 L65 36" stroke="var(--primary)" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M55 30 L55 26" stroke="var(--primary)" strokeWidth="1" strokeLinecap="round" strokeDasharray="2 2" />
    </svg>
  ),
  installed: (
    <svg viewBox="0 0 120 80" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" className="w-full h-full">
      {/* Wall */}
      <rect x="0" y="0" width="120" height="80" fill="var(--stone)" />
      {/* Floor line */}
      <line x1="0" y1="72" x2="120" y2="72" stroke="currentColor" strokeWidth="1" opacity="0.3" />
      {/* Cabinet installed */}
      <rect x="15" y="12" width="90" height="60" rx="1" stroke="currentColor" strokeWidth="1.5" fill="var(--card)" />
      <rect x="19" y="16" width="40" height="52" rx="1" stroke="currentColor" strokeWidth="1" fill="var(--muted)" opacity="0.5" />
      <rect x="61" y="16" width="40" height="52" rx="1" stroke="currentColor" strokeWidth="1" fill="var(--muted)" opacity="0.5" />
      <line x1="54" y1="39" x2="54" y2="45" stroke="var(--primary)" strokeWidth="2" strokeLinecap="round" />
      <line x1="66" y1="39" x2="66" y2="45" stroke="var(--primary)" strokeWidth="2" strokeLinecap="round" />
      {/* Check mark */}
      <circle cx="100" cy="20" r="8" fill="var(--primary)" opacity="0.15" />
      <path d="M96 20 L99 23 L104 17" stroke="var(--primary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  remove: (
    <svg viewBox="0 0 120 80" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" className="w-full h-full">
      {/* Wall */}
      <rect x="0" y="0" width="120" height="80" fill="var(--stone)" />
      {/* Cabinet with one door being removed */}
      <rect x="15" y="12" width="90" height="60" rx="1" stroke="currentColor" strokeWidth="1.5" fill="var(--card)" />
      {/* Left door — being removed, offset */}
      <rect x="22" y="16" width="40" height="52" rx="1" stroke="var(--primary)" strokeWidth="1.5" fill="var(--muted)" opacity="0.4" strokeDasharray="3 2" />
      {/* Right door — stays */}
      <rect x="61" y="16" width="40" height="52" rx="1" stroke="currentColor" strokeWidth="1" fill="var(--muted)" opacity="0.5" />
      {/* Removal arrow */}
      <path d="M19 40 L8 40" stroke="var(--primary)" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M11 37 L8 40 L11 43" stroke="var(--primary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  replace: (
    <svg viewBox="0 0 120 80" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" className="w-full h-full">
      {/* Wall */}
      <rect x="0" y="0" width="120" height="80" fill="var(--stone)" />
      {/* Cabinet */}
      <rect x="15" y="12" width="90" height="60" rx="1" stroke="currentColor" strokeWidth="1.5" fill="var(--card)" />
      {/* New door being placed */}
      <rect x="19" y="16" width="40" height="52" rx="1" stroke="var(--primary)" strokeWidth="1.5" fill="var(--primary)" opacity="0.12" />
      {/* Right door */}
      <rect x="61" y="16" width="40" height="52" rx="1" stroke="currentColor" strokeWidth="1" fill="var(--muted)" opacity="0.5" />
      {/* Handles */}
      <line x1="54" y1="39" x2="54" y2="45" stroke="var(--primary)" strokeWidth="2" strokeLinecap="round" />
      <line x1="66" y1="39" x2="66" y2="45" stroke="var(--primary)" strokeWidth="2" strokeLinecap="round" />
      {/* Replace arrow */}
      <path d="M8 40 L19 40" stroke="var(--primary)" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M16 37 L19 40 L16 43" stroke="var(--primary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      {/* New label */}
      <rect x="22" y="18" width="20" height="8" rx="1" fill="var(--primary)" opacity="0.2" />
      <text x="32" y="24.5" textAnchor="middle" fontSize="5" fill="var(--primary)" fontFamily="sans-serif" fontWeight="600">NEW</text>
    </svg>
  ),
};

export default function ModularityDiagram() {
  const [activeStep, setActiveStep] = useState<StepId>('complete');
  const steps = MODULARITY_SECTION.diagramSteps;
  const activeIndex = STEP_IDS.indexOf(activeStep);
  const activeData = steps[activeIndex];

  return (
    <div className="w-full" aria-label="Modular product interactive diagram">
      {/* Step selector */}
      <div className="flex flex-wrap gap-px bg-border mb-6" role="tablist" aria-label="Modular product stages">
        {steps.map((step, i) => {
          const id = STEP_IDS[i];
          const isActive = id === activeStep;
          return (
            <button
              key={id}
              role="tab"
              aria-selected={isActive}
              aria-controls={`modular-panel-${id}`}
              id={`modular-tab-${id}`}
              onClick={() => setActiveStep(id)}
              className={`flex-1 min-w-[80px] px-3 py-2.5 text-xs font-semibold tracking-wider uppercase transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                isActive
                  ? 'bg-primary text-white' :'bg-card text-muted-foreground hover:bg-stone hover:text-foreground'
              }`}
            >
              {step.label}
            </button>
          );
        })}
      </div>

      {/* Visual panel */}
      <div
        id={`modular-panel-${activeStep}`}
        role="tabpanel"
        aria-labelledby={`modular-tab-${activeStep}`}
        className="bg-stone border border-border"
      >
        {/* Diagram */}
        <div className="p-8 flex items-center justify-center" style={{ minHeight: '200px' }}>
          <div className="w-full max-w-xs text-foreground" style={{ color: 'var(--foreground)' }}>
            {STEP_VISUALS[activeStep]}
          </div>
        </div>

        {/* Step info */}
        <div className="border-t border-border px-6 py-4 flex items-start gap-4">
          <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-primary text-white text-xs font-bold">
            {activeIndex + 1}
          </div>
          <div>
            <p className="text-xs font-semibold tracking-widest uppercase text-primary mb-1">
              {activeData?.label}
            </p>
            <p className="text-sm text-muted-foreground">{activeData?.description}</p>
          </div>
        </div>
      </div>

      {/* Progress indicator */}
      <div className="mt-3 flex gap-1" aria-hidden="true">
        {STEP_IDS.map((id, i) => (
          <div
            key={id}
            className="h-0.5 flex-1 transition-colors duration-200"
            style={{ backgroundColor: i <= activeIndex ? 'var(--primary)' : 'var(--border)' }}
          />
        ))}
      </div>
    </div>
  );
}
